import { useEffect, useState, Fragment } from "react";
import api, { formatApiError } from "../lib/api";
import ImageUpload from "../components/ImageUpload";
import LiveEntryStats from "../components/LiveEntryStats";
import { Loader2, Plus, Trash2, Eye, EyeOff, Ticket, X, QrCode, CalendarDays, Printer, Radio } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtGNF = (n) => `${new Intl.NumberFormat("fr-FR").format(n || 0)} GNF`;

function imgSrc(v) {
  if (!v) return null;
  if (v.startsWith("http") || v.startsWith("/")) return v;
  return `${BACKEND}/api/files/${v}`;
}

export default function AdminTicketing() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null); // event pending delete confirm
  const [historyOf, setHistoryOf] = useState(null); // event for tickets history
  const [braceletsOf, setBraceletsOf] = useState(null); // event for bracelets modal
  const [liveOf, setLiveOf] = useState(null); // event for live entry stats
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/events");
      setEvents(data.events || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (ev) => {
    setBusy(true);
    try {
      await api.patch(`/admin/events/${ev.id}`, { published: !ev.published });
      toast.success(ev.published ? "Événement dépublié" : "Événement publié");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/events/${deleting.id}`);
      toast.success("Événement supprimé");
      setDeleting(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="text-center font-mono text-sm uppercase tracking-wider text-muted-foreground py-12">Chargement…</div>;

  const totalRevenue = events.reduce((s, e) => s + (e.revenue_gnf || 0), 0);
  const totalCommission = events.reduce((s, e) => s + (e.commission_gnf || 0), 0);
  const totalSold = events.reduce((s, e) => s + (e.tickets_sold || 0), 0);

  return (
    <div data-testid="admin-ticketing-page">
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="card-mc p-5 bg-[hsl(var(--muted))]/40 flex-1">
          <p className="overline mb-2">Billetterie · Gestion des événements</p>
          <p className="text-sm">Créez, publiez, dépubliez ou supprimez des événements. Consultez l'historique sécurisé de chaque billet vendu (code unique BA-XXXX-XXXX, anti-fraude, scan unique).</p>
        </div>
        <button data-testid="admin-create-event-btn" onClick={() => setCreating(true)} className="btn-primary whitespace-nowrap">
          <Plus className="w-4 h-4 mr-2" /> Créer un événement
        </button>
      </div>

      <div className="grid grid-cols-3 gap-0 card-mc mb-6" data-testid="ticketing-kpis">
        {[
          { label: "Billets vendus", value: totalSold },
          { label: "Revenus billetterie", value: fmtGNF(totalRevenue) },
          { label: "Commissions plateforme", value: fmtGNF(totalCommission) },
        ].map((c, i) => (
          <div key={i} className="p-5 border-r border-border last:border-r-0">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{c.label}</p>
            <p className="mt-1 font-display font-black text-2xl tracking-tighter">{c.value}</p>
          </div>
        ))}
      </div>

      {events.length === 0 ? (
        <div className="card-mc p-12 text-center text-muted-foreground" data-testid="ticketing-empty">Aucun événement pour le moment.</div>
      ) : (
        <div className="card-mc overflow-x-auto" data-testid="admin-events-table">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                {["Événement", "Artiste", "Date", "Statut", "Vendus", "Scannés", "Revenus", "Commission", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <Fragment key={ev.id}>
                <tr className="border-t border-border" data-testid={`admin-event-row-${ev.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {imgSrc(ev.cover_image) && <img src={imgSrc(ev.cover_image)} alt="" className="w-10 h-10 object-cover rounded-sm shrink-0" />}
                      <div>
                        <p className="font-bold">{ev.title}</p>
                        <p className="text-[10px] font-mono text-muted-foreground">{ev.venue} · {ev.city}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs">{ev.artist_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{ev.event_date ? new Date(ev.event_date).toLocaleDateString("fr-FR") : "—"}</td>
                  <td className="px-4 py-3">
                    {ev.published ? <span className="status-badge bg-[hsl(var(--accent))] text-white">Publié</span> : <span className="status-badge bg-muted">Brouillon</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{ev.tickets_sold}</td>
                  <td className="px-4 py-3 font-mono text-xs">{ev.tickets_scanned}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtGNF(ev.revenue_gnf)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtGNF(ev.commission_gnf)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button data-testid={`event-history-${ev.id}`} disabled={busy} onClick={() => setHistoryOf(ev)} title="Historique des billets" className="btn-outline text-[11px] px-2 py-1.5 inline-flex items-center gap-1">
                        <Ticket className="w-3 h-3" /> Billets
                      </button>
                      <button data-testid={`event-bracelets-${ev.id}`} disabled={busy} onClick={() => setBraceletsOf(ev)} title="Bracelets QR imprimables" className="btn-outline text-[11px] px-2 py-1.5 inline-flex items-center gap-1">
                        <QrCode className="w-3 h-3" /> Bracelets
                      </button>
                      <button data-testid={`event-live-${ev.id}`} disabled={busy} onClick={() => setLiveOf(ev)} title="Entrées en direct" className="btn-secondary text-[11px] px-2 py-1.5 inline-flex items-center gap-1">
                        <Radio className="w-3 h-3" /> Direct
                      </button>
                      <button data-testid={`event-publish-${ev.id}`} disabled={busy} onClick={() => togglePublish(ev)} title={ev.published ? "Dépublier" : "Publier"} className="btn-secondary text-[11px] px-2 py-1.5 inline-flex items-center gap-1">
                        {ev.published ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />} {ev.published ? "Dépublier" : "Publier"}
                      </button>
                      <button data-testid={`event-delete-${ev.id}`} disabled={busy || ev.tickets_sold > 0} onClick={() => setDeleting(ev)}
                        title={ev.tickets_sold > 0 ? "Impossible : billets vendus. Dépubliez à la place." : "Supprimer"}
                        className="btn-outline text-[11px] px-2 py-1.5 inline-flex items-center gap-1 text-destructive border-destructive disabled:opacity-40">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="delete-event-modal">
          <div className="bg-card card-mc w-full max-w-md p-6">
            <h3 className="font-display font-black text-xl tracking-tighter mb-2">Supprimer « {deleting.title} » ?</h3>
            <p className="text-sm text-muted-foreground">Cette action est définitive. L'événement, ses types de billets et ses codes contrôleurs seront supprimés.</p>
            <div className="flex gap-2 justify-end mt-6">
              <button data-testid="cancel-delete-event-btn" onClick={() => setDeleting(null)} className="btn-ghost text-sm">Annuler</button>
              <button data-testid="confirm-delete-event-btn" disabled={busy} onClick={confirmDelete} className="btn-primary text-sm bg-destructive">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {creating && <CreateEventModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {historyOf && <TicketsHistoryModal event={historyOf} onClose={() => setHistoryOf(null)} />}
      {braceletsOf && <BraceletsModal event={braceletsOf} onClose={() => { setBraceletsOf(null); load(); }} />}
      {liveOf && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="live-stats-modal">
          <div className="bg-card card-mc w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="overline mb-1">Entrées en direct</p>
                <h3 className="font-display font-black text-2xl tracking-tighter">{liveOf.title}</h3>
              </div>
              <button data-testid="close-live-stats-btn" onClick={() => setLiveOf(null)} className="btn-ghost p-2"><X className="w-4 h-4" /></button>
            </div>
            <LiveEntryStats eventId={liveOf.id} />
          </div>
        </div>
      )}
    </div>
  );
}

function CreateEventModal({ onClose, onCreated }) {
  const [artists, setArtists] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ artist_id: "", title: "", description: "", venue: "", city: "", event_date: "", cover_image: "" });
  const [types, setTypes] = useState([{ name: "Standard", price_gnf: 100000, quantity_total: 100, description: "" }]);

  useEffect(() => {
    api.get("/admin/artists").then((r) => setArtists(r.data.artists || [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const setT = (i, k, v) => setTypes((ts) => ts.map((t, j) => (j === i ? { ...t, [k]: v } : t)));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.artist_id) return toast.error("Sélectionnez un artiste");
    if (types.length === 0) return toast.error("Ajoutez au moins un type de billet");
    setBusy(true);
    try {
      await api.post("/admin/events", {
        ...form,
        ticket_types: types.map((t, i) => ({ ...t, price_gnf: Number(t.price_gnf), quantity_total: Number(t.quantity_total), sort_order: i })),
      });
      toast.success("Événement créé (brouillon). Publiez-le quand il est prêt.");
      onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full border border-border px-3 py-2 rounded-sm bg-card text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-event-modal">
      <form onSubmit={submit} className="bg-card card-mc w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-display font-black text-2xl tracking-tighter">Créer un événement</h3>
            <p className="text-xs text-muted-foreground mt-1">L'événement est créé au nom de l'artiste sélectionné, en brouillon.</p>
          </div>
          <button type="button" data-testid="close-create-event-btn" onClick={onClose} className="btn-ghost p-2"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Artiste *</label>
            <select data-testid="create-event-artist" required value={form.artist_id} onChange={(e) => setForm({ ...form, artist_id: e.target.value })} className={inputCls}>
              <option value="">— Sélectionner un artiste —</option>
              {artists.map((a) => <option key={a.id} value={a.id}>{a.stage_name} ({a.category})</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Titre *</label>
            <input data-testid="create-event-title" required minLength={2} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Grand concert live à Conakry" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Description</label>
            <textarea data-testid="create-event-description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Lieu *</label>
            <input data-testid="create-event-venue" required minLength={2} value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className={inputCls} placeholder="Palais du Peuple" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Ville *</label>
            <input data-testid="create-event-city" required minLength={2} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} placeholder="Conakry" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Date & heure *</label>
            <input data-testid="create-event-date" required type="datetime-local" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} className={inputCls} />
          </div>
          <div className="md:col-span-2">
            <ImageUpload value={form.cover_image} onChange={(v) => setForm({ ...form, cover_image: v })} label="Image de couverture" testid="create-event-cover" aspect="aspect-[16/7]" />
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <p className="font-display font-bold">Types de billets *</p>
            <button type="button" data-testid="add-ticket-type-btn" onClick={() => setTypes([...types, { name: "", price_gnf: 0, quantity_total: 50, description: "" }])} className="btn-outline text-xs px-2.5 py-1.5">
              <Plus className="w-3 h-3 mr-1" /> Ajouter
            </button>
          </div>
          <div className="space-y-2">
            {types.map((t, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end card-mc p-3" data-testid={`ticket-type-row-${i}`}>
                <div className="col-span-4">
                  <label className="text-[10px] font-mono uppercase text-muted-foreground">Nom</label>
                  <input required value={t.name} onChange={(e) => setT(i, "name", e.target.value)} className={inputCls} placeholder="VIP, Carré Or…" data-testid={`ticket-type-name-${i}`} />
                </div>
                <div className="col-span-4">
                  <label className="text-[10px] font-mono uppercase text-muted-foreground">Prix (GNF)</label>
                  <input required type="number" min={0} value={t.price_gnf} onChange={(e) => setT(i, "price_gnf", e.target.value)} className={inputCls} data-testid={`ticket-type-price-${i}`} />
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] font-mono uppercase text-muted-foreground">Quantité</label>
                  <input required type="number" min={1} value={t.quantity_total} onChange={(e) => setT(i, "quantity_total", e.target.value)} className={inputCls} data-testid={`ticket-type-qty-${i}`} />
                </div>
                <div className="col-span-1">
                  <button type="button" data-testid={`remove-ticket-type-${i}`} disabled={types.length === 1} onClick={() => setTypes(types.filter((_, j) => j !== i))} className="btn-ghost p-2 text-destructive disabled:opacity-30">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Annuler</button>
          <button type="submit" data-testid="submit-create-event-btn" disabled={busy} className="btn-primary text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CalendarDays className="w-4 h-4 mr-1" />} Créer l'événement
          </button>
        </div>
      </form>
    </div>
  );
}

function openPrintWindow({ event, batch, tickets }) {
  const dateStr = event?.event_date ? new Date(event.event_date).toLocaleDateString("fr-FR") : "";
  const cells = tickets.map((t) => `
    <div class="cell${t.status !== "valid" ? " off" : ""}">
      <img src="${t.qr}" alt="QR" />
      <div class="code">${t.code}</div>
      <div class="meta">${batch.ticket_type_name}${t.status === "used" ? " · UTILISÉ" : t.status === "cancelled" ? " · ANNULÉ" : ""}</div>
    </div>`).join("");
  const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>Bracelets · ${event?.title || ""}</title>
    <style>
      body { font-family: Arial, sans-serif; margin: 16px; }
      h1 { font-size: 16px; margin: 0 0 2px; }
      p.sub { font-size: 11px; color: #555; margin: 0 0 14px; }
      .grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; }
      .cell { border: 1px dashed #999; padding: 6px; text-align: center; page-break-inside: avoid; }
      .cell.off { opacity: 0.35; }
      .cell img { width: 88px; height: 88px; }
      .code { font-family: monospace; font-weight: bold; font-size: 11px; margin-top: 2px; }
      .meta { font-size: 9px; color: #555; }
      @media print { .noprint { display: none; } }
    </style></head><body>
    <div class="noprint" style="margin-bottom:10px"><button onclick="window.print()" style="padding:8px 16px;font-weight:bold;cursor:pointer">🖨️ Imprimer</button></div>
    <h1>${event?.title || ""} — ${batch.label}</h1>
    <p class="sub">${event?.venue || ""} · ${dateStr} · ${tickets.length} bracelet(s) · Type : ${batch.ticket_type_name} · Chaque QR est unique et à usage unique — à découper et coller sur les bracelets.</p>
    <div class="grid">${cells}</div>
    </body></html>`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Autorisez les fenêtres pop-up pour imprimer"); return; }
  w.document.write(html);
  w.document.close();
}

function BraceletsModal({ event, onClose }) {
  const [batches, setBatches] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [form, setForm] = useState({ ticket_type_id: event.ticket_types?.[0]?.id || "", quantity: 50, label: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/events/${event.id}/bracelets`);
      setBatches(data.batches || []);
    } catch (e) { toast.error(formatApiError(e)); setBatches([]); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    if (!form.ticket_type_id) return toast.error("Sélectionnez un type de billet");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${event.id}/bracelets`, {
        ticket_type_id: form.ticket_type_id, quantity: Number(form.quantity), label: form.label,
      });
      toast.success(`${data.tickets.length} bracelet(s) généré(s) — planche d'impression ouverte`);
      openPrintWindow({ event: data.event, batch: data.batch, tickets: data.tickets });
      setForm((f) => ({ ...f, label: "" }));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const reprint = async (b) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/bracelets/${b.id}/print`);
      openPrintWindow(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancelBatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bracelets/${cancelling.id}/cancel`);
      toast.success(`Lot annulé — ${data.cancelled} bracelet(s) invalidé(s), stock libéré`);
      setCancelling(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="bracelets-modal">
      <div className="bg-card card-mc w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="overline mb-1">Bracelets QR · Entrées physiques</p>
            <h3 className="font-display font-black text-2xl tracking-tighter">{event.title}</h3>
            <p className="text-xs text-muted-foreground mt-1">Chaque bracelet reçoit un QR unique signé + code BA-XXXX-XXXX, à usage unique (même sécurité que les billets en ligne). Le stock de l'événement est décompté.</p>
          </div>
          <button data-testid="close-bracelets-btn" onClick={onClose} className="btn-ghost p-2"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={generate} className="card-mc p-4 bg-[hsl(var(--muted))]/40 grid grid-cols-1 md:grid-cols-4 gap-3 items-end mb-6">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Type de billet</label>
            <select data-testid="bracelet-type-select" value={form.ticket_type_id} onChange={(e) => setForm({ ...form, ticket_type_id: e.target.value })} className="w-full border border-border px-3 py-2 rounded-sm bg-card text-sm">
              {(event.ticket_types || []).map((tt) => (
                <option key={tt.id} value={tt.id}>{tt.name} ({tt.quantity_total - tt.quantity_sold} restants)</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Quantité (max 500)</label>
            <input data-testid="bracelet-qty-input" required type="number" min={1} max={500} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="w-full border border-border px-3 py-2 rounded-sm bg-card text-sm" />
          </div>
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Libellé du lot</label>
            <input data-testid="bracelet-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Bracelets VIP soirée" className="w-full border border-border px-3 py-2 rounded-sm bg-card text-sm" />
          </div>
          <button type="submit" data-testid="generate-bracelets-btn" disabled={busy} className="btn-primary text-sm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <QrCode className="w-4 h-4 mr-1" />} Générer & imprimer
          </button>
        </form>

        <p className="overline mb-3">Lots générés</p>
        {batches === null ? (
          <div className="py-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : batches.length === 0 ? (
          <div className="card-mc p-8 text-center text-muted-foreground text-sm" data-testid="bracelets-empty">Aucun lot de bracelets pour cet événement.</div>
        ) : (
          <div className="space-y-2" data-testid="bracelets-batches-list">
            {batches.map((b) => (
              <div key={b.id} className="card-mc p-4 flex flex-wrap items-center gap-3" data-testid={`bracelet-batch-${b.id}`}>
                <div className="flex-1 min-w-[180px]">
                  <p className="font-bold text-sm">{b.label} {b.status === "cancelled" && <span className="status-badge bg-destructive text-white ml-1">Annulé</span>}</p>
                  <p className="text-[11px] font-mono text-muted-foreground">{b.ticket_type_name} · {b.quantity} bracelets · {new Date(b.created_at).toLocaleString("fr-FR")}</p>
                </div>
                <div className="flex gap-3 text-xs font-mono">
                  <span className="text-[hsl(var(--accent))]" data-testid={`batch-valid-${b.id}`}>{b.valid_count} valides</span>
                  <span data-testid={`batch-used-${b.id}`}>{b.used_count} scannés</span>
                  {b.cancelled_count > 0 && <span className="text-destructive">{b.cancelled_count} annulés</span>}
                </div>
                <div className="flex gap-1.5">
                  <button data-testid={`reprint-batch-${b.id}`} disabled={busy} onClick={() => reprint(b)} className="btn-outline text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1">
                    <Printer className="w-3 h-3" /> Imprimer
                  </button>
                  {b.status !== "cancelled" && b.valid_count > 0 && (
                    <button data-testid={`cancel-batch-${b.id}`} disabled={busy} onClick={() => setCancelling(b)} className="btn-outline text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1 text-destructive border-destructive">
                      <Trash2 className="w-3 h-3" /> Annuler le lot
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {cancelling && (
          <div className="mt-4 card-mc p-4 bg-destructive/5" data-testid="cancel-batch-confirm">
            <p className="text-sm font-bold text-destructive">Annuler le lot « {cancelling.label} » ?</p>
            <p className="text-xs text-muted-foreground mt-1">Les {cancelling.valid_count} bracelet(s) non utilisé(s) seront définitivement invalidés (refusés au scan) et le stock sera libéré. Les bracelets déjà scannés restent dans l'historique.</p>
            <div className="flex gap-2 mt-3 justify-end">
              <button data-testid="cancel-cancel-batch-btn" onClick={() => setCancelling(null)} className="btn-ghost text-xs">Retour</button>
              <button data-testid="confirm-cancel-batch-btn" disabled={busy} onClick={cancelBatch} className="btn-primary text-xs bg-destructive">
                {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />} Confirmer l'annulation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TicketsHistoryModal({ event, onClose }) {
  const [tickets, setTickets] = useState(null);

  useEffect(() => {
    api.get(`/admin/events/${event.id}/tickets`).then((r) => setTickets(r.data.tickets || [])).catch((e) => { toast.error(formatApiError(e)); setTickets([]); });
  }, [event.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="tickets-history-modal">
      <div className="bg-card card-mc w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="overline mb-1">Historique des billets</p>
            <h3 className="font-display font-black text-2xl tracking-tighter">{event.title}</h3>
            <p className="text-xs text-muted-foreground mt-1">Chaque billet possède un code unique infalsifiable (BA-XXXX-XXXX) + QR signé, à usage unique.</p>
          </div>
          <button data-testid="close-history-btn" onClick={onClose} className="btn-ghost p-2"><X className="w-4 h-4" /></button>
        </div>

        {tickets === null ? (
          <div className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
        ) : tickets.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground" data-testid="history-empty">Aucun billet pour cet événement.</div>
        ) : (
          <div className="overflow-x-auto" data-testid="tickets-history-table">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  {["Code billet", "Type", "Acheteur", "Prix", "Statut", "Acheté le", "Scanné le"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <tr key={t.id} className="border-t border-border" data-testid={`history-ticket-${t.id}`}>
                    <td className="px-3 py-2.5 font-mono text-xs font-bold inline-flex items-center gap-1.5"><QrCode className="w-3.5 h-3.5" />{t.code || t.id.slice(0, 8).toUpperCase()}</td>
                    <td className="px-3 py-2.5 text-xs">{t.ticket_type_name}</td>
                    <td className="px-3 py-2.5 text-xs">{t.buyer_name}<br /><span className="text-[10px] font-mono text-muted-foreground">{t.buyer_email}</span></td>
                    <td className="px-3 py-2.5 font-mono text-xs">{fmtGNF(t.price_gnf)}</td>
                    <td className="px-3 py-2.5">
                      {t.status === "used" ? <span className="status-badge bg-muted text-muted-foreground">Scanné ✓</span>
                        : t.status === "valid" ? <span className="status-badge bg-[hsl(var(--accent))] text-white">Valide</span>
                        : <span className="status-badge bg-[hsl(var(--secondary))]">{t.status}</span>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-[11px]">{t.created_at ? new Date(t.created_at).toLocaleString("fr-FR") : "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-[11px]">{t.scanned_at ? new Date(t.scanned_at).toLocaleString("fr-FR") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
